import { DataGridPremiumProps } from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  DataGridCRUDConfig,
  useDataGridCRUDValidation,
  useDataGridRowFileChangesState,
} from '~/components/SolaForceDataGrid/hooks';
import { useGetCompanyForms } from '~/data/documents/useGetCompanyForms';
import {
  deleteEmployeeForm,
  saveEmployeeForm,
  useGetEmployeeForms,
} from '~/data/documents/useGetEmployeeForms';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  CompanyFormsEntry,
  EmployeeFormsEntry,
  FormsEntryType,
  convertCompanyFormTypeToCompanyFormsEntry,
  convertEmployeeFormTypeToEmployeeFormsEntry,
  convertEmployeeFormsEntryToSubmission,
  employeeFormSubmissionToFormData,
} from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/FormsTable/data';
import { UseFormsTableColumnsProps } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/FormsTable/useFormsTableColumns';

export type FormsTablePermissions = {
  canEdit: boolean;
  canDownloadCompanyForms: boolean;
};

type UseFormsTableOutput = {
  isLoading: boolean;
  dataGridProps: Omit<DataGridPremiumProps<EmployeeFormsEntry>, 'columns'>;
  downloadDialogConfig: {
    canDownloadCompanyForms: boolean;
    employeeId: number;
    companyFormsEntries: CompanyFormsEntry[];
  };
  columnsConfig: UseFormsTableColumnsProps;
  crudConfig: DataGridCRUDConfig<EmployeeFormsEntry, EmployeeFormsEntry['id']>;
};

export const useFormsTable = (): UseFormsTableOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const {
    selectedEmployeeId,
    // selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  const permissions: FormsTablePermissions = useMemo(() => {
    return {
      canEdit: true,
      canDownloadCompanyForms: true,
    };
  }, []);

  // Company Forms Data
  const {
    data: companyFormsData,
    isLoading: isLoadingCompanyForms,
    mutate: mutateCompanyForms,
  } = useGetCompanyForms();

  const companyFormsEntries = useMemo(() => {
    return companyFormsData.map(convertCompanyFormTypeToCompanyFormsEntry);
  }, [companyFormsData]);

  // Employee Forms Data
  const {
    data: formsData,
    isLoading: isLoadingEmployeeForms,
    mutate: mutateEmployeeForms,
  } = useGetEmployeeForms({ employeeId: selectedEmployeeId });

  const { employeeFormsEntries } = useMemo(() => {
    return {
      employeeFormsEntries: formsData.map(
        convertEmployeeFormTypeToEmployeeFormsEntry,
      ),
    };
  }, [formsData]);

  // Temporary storage for updated files
  const { fileChanges, updateFileChanges, resetFileChange } =
    useDataGridRowFileChangesState();

  const includeFileChangesToSubmission = useCallback(
    (submitRow: EmployeeFormsEntry): EmployeeFormsEntry => {
      const fileChange = fileChanges.find((fc) => fc.id === submitRow.id);
      if (!!fileChange) {
        const { file } = fileChange;

        return {
          ...submitRow,
          attachment: file,
          attachmentName: file?.name ?? '',
        };
      }

      return submitRow;
    },
    [fileChanges],
  );

  const validationSchema = yup.object().shape<EmployeeFormsEntry>({
    id: yup.number(),
    type: yup.string(),
    employeeId: yup.number(),
    companyFormId: yup
      .number()
      .required(t('laFillManFields'))
      .oneOf(
        companyFormsEntries.map((cfe) => cfe.id),
        t('laFillManFields'),
      ),
    attachmentId: yup.number(),
    attachmentName: yup.string().required(t('laFillManFields')),
    attachmentIsArchived: yup.boolean(),
    description: yup.string(),
  });

  const handleSubmitPromise = useCallback(
    async <T>(promise: Promise<T>) => {
      return promise
        .then((result) => {
          mutateEmployeeForms();
          mutateCompanyForms();
          enqueueSnackbar(t('change_saved'), { variant: 'success' });
          return Promise.resolve(result);
        })
        .catch((error) => {
          enqueueSnackbar(t('laFailedToSave'), { variant: 'error' });
          return Promise.reject(error);
        });
    },
    [t, enqueueSnackbar, mutateEmployeeForms, mutateCompanyForms],
  );

  // Handle Add New & Update
  const handleSubmit = useCallback(
    async (
      submitRow: EmployeeFormsEntry,
      method: 'POST' | 'UPDATE',
    ): Promise<EmployeeFormsEntry> => {
      const submitData = includeFileChangesToSubmission(submitRow);

      return validationSchema
        .validate(submitData, { abortEarly: false })
        .then(async (): Promise<EmployeeFormsEntry> => {
          const isUpdate = method === 'UPDATE';
          const submission = convertEmployeeFormsEntryToSubmission(
            submitData,
            isUpdate,
          );

          return handleSubmitPromise(
            saveEmployeeForm(
              selectedEmployeeId,
              employeeFormSubmissionToFormData(submission),
              isUpdate ? submission.fEmpFormId : undefined,
            ),
          ).then((result) => {
            resetFileChange(submitRow.id);
            return Promise.resolve(
              convertEmployeeFormTypeToEmployeeFormsEntry(result),
            );
          });
        })
        .catch((err) => {
          return Promise.reject({ rowId: submitRow.id, error: err });
        });
    },
    [
      validationSchema,
      includeFileChangesToSubmission,
      handleSubmitPromise,
      selectedEmployeeId,
      resetFileChange,
    ],
  );

  // Handle Delete
  const onSubmitDelete = useCallback(
    async (deletedRow: EmployeeFormsEntry) => {
      return handleSubmitPromise(
        deleteEmployeeForm(selectedEmployeeId, deletedRow.id),
      ).then(() => {
        resetFileChange(deletedRow.id);
      });
    },
    [handleSubmitPromise, selectedEmployeeId, resetFileChange],
  );

  const { actionsValidation, columnsValidation, dataGridProps } =
    useDataGridCRUDValidation<EmployeeFormsEntry>({
      validationSchema,
    });

  const crudConfig: DataGridCRUDConfig<
    EmployeeFormsEntry,
    EmployeeFormsEntry['id']
  > = {
    data: {
      onSubmitCreate: (createdRow: EmployeeFormsEntry) =>
        handleSubmit(createdRow, 'POST'),
      onSubmitUpdate: (createdRow: EmployeeFormsEntry) =>
        handleSubmit(createdRow, 'UPDATE'),
      onSubmitDelete,
      onCancelEdit: (editedRow) => {
        resetFileChange(editedRow.id);
      },
    },
    getRowId: (row) => row.id,
    addNewRow: {
      getNewId: (rows) => rows.length * -1,
      getInitialRowData: (
        newId: EmployeeFormsEntry['id'],
      ): EmployeeFormsEntry => {
        return {
          id: newId,
          type: FormsEntryType.Employee,
          companyFormId: -1,
          employeeId: selectedEmployeeId,
          attachmentId: -1,
          attachmentName: '',
          attachmentIsArchived: false,
          description: '',
          attachment: undefined,
        };
      },
      fieldToFocus: 'companyFormId',
    },
    isRowEditable: (params) => {
      return !params.row.attachmentIsArchived;
    },
    getDeleteRowMessage: (row) => {
      return `${t('delete')}: ${row.attachmentName}`;
    },
    actionsValidation,
    permissions: {
      canAddNew: permissions.canEdit,
    },
  };

  return {
    isLoading: isLoadingEmployeeForms || isLoadingCompanyForms,
    downloadDialogConfig: {
      canDownloadCompanyForms: permissions.canDownloadCompanyForms,
      employeeId: selectedEmployeeId,
      companyFormsEntries,
    },
    columnsConfig: {
      selectedEmployeeId,
      companyFormsEntries,
      canEdit: permissions.canEdit,
      columnsValidation,
      updateFileChanges,
      fileChanges,
    },
    crudConfig,
    dataGridProps: {
      ...dataGridProps,
      rows: employeeFormsEntries,
    },
  };
};
